/**
 * UserCommentCard.tsx — 8211-B
 *
 * User comment card component with collapsed/expanded states, inline edit,
 * and delete functionality. Mirrors the FeedbackCard design system but
 * shows the author name instead of a category label.
 *
 * States:
 * - Collapsed Default: author name + truncated text
 * - Collapsed Hover: reveals action icons (resolve, three-dot)
 * - Expanded: full text, three-dot menu with Edit/Delete
 * - Edit Mode: inline textarea with Save/Cancel
 *
 * Reply mechanism uses the shared ReplyBox and Reply components from
 * client/src/components/Comment/ to match the original Comment.js behavior.
 */
import React, { useEffect, useRef, useState } from "react";
import SVG from "react-inlinesvg";
import IconButton from "../IconButton/index.tsx";
import DotsActionMenu from "../DotsActionMenu/index.tsx";
import { useCommentStore } from "../../stores/commentStore";
import { useSudowriteStore } from "../../stores/sudowriteStore";
import ReplyBox from "../Comment/ReplyBox";
import Reply from "../Comment/Reply.js";
import { isCommentEdited } from "../Comment/isCommentEdited";
import { CommentReplies } from "../Comment/styles.js";
import { useToast } from "../ToastWrapper/index.js";
import { cancelReply, submitReply } from "../Comment/replySubmission";
import { getRepliesForRendering } from "../Comment/sharedReplyVisibility";
import { CardContainer, CardHeaderRow, CategoryLabelGroup, CategoryLabel, CardTextContent, HoverActions, EditedLabel, CollapsedReplyIndicator } from "./styles";
import { EditTextarea, EditActions, EditActionButton } from "./userCommentStyles";
import { CommentCardMarkdown } from "./CommentCardMarkdown";
import { DeleteCommentConfirmPortal } from "./DeleteCommentConfirmPortal";
import { CommentAvatar } from "./CommentAvatar";
import { sendAppEvent } from "src/Utils";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UserComment {
  id: string;
  annotationId: string;
  aiId: string | null;
  content: string;
  original?: string | null;
  suggestion?: string | null;
  metadata?: Record<string, unknown>;
  createdAt: string;
  updatedAt?: string;
  isEdited?: boolean;
  replies: unknown[];
  displayName?: string;
  userId?: string;
  photoURL?: string | null;
  [key: string]: unknown;
}

interface UserCommentCardProps {
  /** The comment object */
  comment: UserComment;
  /** Whether this card is currently expanded (accordion) */
  isExpanded: boolean;
  /** Called with comment.id to expand, or null to collapse */
  onExpand: (id: string | null) => void;
  /** Called when clicking an already-expanded card to scroll its annotation into view */
  onScrollToAnnotation?: (commentId: string) => void;
  /** Called when user clicks Delete */
  onDelete: (commentId: string, annotationId: string) => void;
  /** Document ID needed for submitting replies */
  documentId?: string | null;
  /** Location label for analytics (e.g. "Editor") */
  commentLocation?: string;
  /** Whether the app is in mobile mode */
  isMobile?: boolean;
  /** Whether to play the wiggle animation (e.g., when opened from chat link) */
  shouldWiggle?: boolean;
  /** Show only delete action in the header (used in resolved/ignored views) */
  showDeleteOnlyAction?: boolean;
  /** Current sidebar filter tab (analytics for delete confirms) */
  commentFilterForAnalytics?: string;
  /** Called when user clicks Undo from resolved/ignored views */
  onUndo?: (comment: UserComment) => void;
  /** Whether undo is currently possible for this card */
  canUndo?: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function UserCommentCard({
  comment,
  isExpanded,
  onExpand,
  onScrollToAnnotation,
  onDelete,
  documentId,
  commentLocation = "Editor",
  isMobile = false,
  shouldWiggle = false,
  showDeleteOnlyAction = false,
  commentFilterForAnalytics,
  onUndo,
  canUndo = false,
}: UserCommentCardProps): JSX.Element {
  const replySubmitInFlightRef = useRef(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(comment.content);
  const [isEditingReply, setIsEditingReply] = useState(false);
  const [newReply, setNewReply] = useState("");
  const [isSubmittingReply, setIsSubmittingReply] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const newCommentInputBoxRef = useRef<HTMLTextAreaElement>(null);

  const updateComment = useCommentStore((state: { updateComment: (id: string, content: string) => void }) => state.updateComment);
  const resolveComment = useCommentStore((state: { resolveComment: (annotationId: string) => Promise<{ success: boolean }> }) => state.resolveComment);
  const submitComment = useCommentStore((state: { submitComment: (documentId: string, content: string, parent?: unknown) => Promise<{ success: boolean; message: string }> }) => state.submitComment);
  const commenter = useCommentStore((state: { commenter: { uid: string } | null }) => state.commenter);
  const isDocumentOwner = useCommentStore((state: { isDocumentOwner: boolean }) => state.isDocumentOwner);
  const setActiveAnnotationIds = useCommentStore((state: any) => state.setActiveAnnotationIds);
  const setCommentScrollSyncSuppressedUntil = useCommentStore((state: any) => state.setCommentScrollSyncSuppressedUntil);
  const revealAnnotation = useCommentStore((state) => state.revealAnnotation);
  const hoverAnnotation = useCommentStore((state) => state.hoverAnnotation);
  const hoveredAnnotationIds = useCommentStore((state: any) => state.hoveredAnnotationIds);
  const activeAnnotationIds = useCommentStore((state: any) => state.activeAnnotationIds);
  const isSharedDocument = useCommentStore((state: { isSharedDocument?: boolean }) => state.isSharedDocument === true);
  const hasAccessToAgenticChat = useSudowriteStore((state) => state.subscriptionJSON?.hasAccessToAgenticChat ?? state.subscription?.hasAccessToAgenticChat ?? false);
  const [isHovered, setIsHovered] = useState(false);
  const { setToast } = useToast();

  // Reveal the gold highlight when the card mounts
  useEffect(() => {
    return revealAnnotation(comment.annotationId);
  }, [comment.annotationId, revealAnnotation]);

  // Apply brighter hover highlight when hovered or expanded
  useEffect(() => {
    if (isHovered || isExpanded || hoveredAnnotationIds?.includes(comment.annotationId) || activeAnnotationIds?.includes(comment.annotationId)) {
      return hoverAnnotation(comment.annotationId);
    }
  }, [isHovered, isExpanded, hoveredAnnotationIds, activeAnnotationIds, comment.annotationId, hoverAnnotation]);

  const authorName = comment.displayName || "Anonymous";
  const hasContent = comment.content != null && comment.content.trim() !== "";
  const visibleReplies = getRepliesForRendering({
    replies: comment.replies,
    isSharedDocument,
  });
  const replyCountLabel = visibleReplies.length === 1 ? "1 reply" : `${visibleReplies.length} replies`;
  const replyPlaceholder = !isSharedDocument && hasAccessToAgenticChat ? "Comment or @ to ask Sudowrite" : "Reply";
  const isEdited = isCommentEdited(comment);
  const isOwner = commenter?.uid === comment.userId;
  const canManage = isOwner || isDocumentOwner;

  // Click handlers
  const handleCardClick = () => {
    if (!isExpanded) {
      onExpand(comment.id);
    } else if (onScrollToAnnotation) {
      // If already expanded, scroll the annotation back into view
      onScrollToAnnotation(comment.id);
    }
  };

  const handleHeaderClick = (e: React.MouseEvent) => {
    if (isExpanded) {
      e.stopPropagation();
      onExpand(null);
      // Prevent scroll-sync from instantly re-opening the same comment after manual collapse.
      setCommentScrollSyncSuppressedUntil?.(Date.now() + 2000);
    }
  };

  const handleEdit = () => {
    setEditContent(comment.content);
    setIsEditing(true);
  };

  const handleSave = () => {
    updateComment(comment.id, editContent);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditContent(comment.content);
    setIsEditing(false);
  };

  const handleDelete = () => {
    if (showDeleteOnlyAction) {
      setShowDeleteConfirm(true);
      return;
    }
    onDelete(comment.id, comment.annotationId);
  };

  const handleUndo = () => {
    if (!canUndo || !onUndo) return;
    sendAppEvent("Comment - Undo Clicked", { comment_id: comment.id });
    onUndo(comment);
  };

  const handleConfirmDelete = () => {
    setShowDeleteConfirm(false);
    sendAppEvent("Comment - Delete Confirmed", {
      comment_id: comment.id,
      filter_type: commentFilterForAnalytics,
    });
    onDelete(comment.id, comment.annotationId);
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  const handleResolve = () => {
    resolveComment(comment.annotationId);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      handleSave();
    }
    if (e.key === "Escape") {
      handleCancel();
    }
  };

  // -----------------------------------------------------------------------
  // Reply handlers — matches Comment.js handleReplySubmit / handleReplyCancel
  // -----------------------------------------------------------------------

  const handleReplySubmit = async (e: React.FormEvent | React.MouseEvent) => {
    e.preventDefault();
    await submitReply({
      newReply,
      isSubmittingReply,
      replySubmitInFlightRef,
      isMobile,
      setActiveAnnotationIds,
      setToast,
      documentId,
      commentLocation,
      submitComment,
      comment,
      setNewReply,
      setIsSubmittingReply,
      onSubmitFailed: (restoreReply, message) => {
        setToast(message);
        setNewReply(restoreReply);
      },
    });
  };

  const handleReplyCancel = () => {
    cancelReply({
      isSubmittingReply,
      setNewReply,
    });
  };

  return (
    <CardContainer
      data-testid="user-comment-card"
      $isExpanded={isExpanded}
      $shouldWiggle={shouldWiggle}
      aria-expanded={isExpanded}
      onClick={handleCardClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Header */}
      <CardHeaderRow
        data-testid="user-comment-header"
        onClick={handleHeaderClick}
      >
        <CategoryLabelGroup>
          <CommentAvatar
            kind="user"
            label={authorName}
            photoURL={comment.photoURL}
          />
          <CategoryLabel>{authorName}</CategoryLabel>
        </CategoryLabelGroup>

        {isExpanded && canManage && (
          <HoverActions style={{ opacity: 1 }}>
            {showDeleteOnlyAction ? (
              <>
                {canUndo && (
                  <IconButton
                    data-testid="user-comment-undo-btn"
                    src="/assets/formatting-undo.svg"
                    size={12}
                    iconSize={14}
                    hoverTipText="Undo"
                    ariaLabel="Undo"
                    onClick={(e: React.MouseEvent | React.KeyboardEvent) => {
                      e.stopPropagation();
                      handleUndo();
                    }}
                  />
                )}
                <IconButton
                  data-testid="user-comment-delete-btn"
                  src="/assets/delete.svg"
                  size={12}
                  hoverTipText="Delete"
                  ariaLabel="Delete"
                  onClick={(e: React.MouseEvent | React.KeyboardEvent) => {
                    e.stopPropagation();
                    handleDelete();
                  }}
                />
              </>
            ) : (
              <>
                <IconButton
                  data-testid="user-comment-resolve-btn"
                  src="/assets/check-simple.svg"
                  size={12}
                  hoverTipText="Resolve"
                  ariaLabel="Resolve"
                  onClick={(e: React.MouseEvent | React.KeyboardEvent) => {
                    e.stopPropagation();
                    handleResolve();
                  }}
                />
                {isOwner && (
                  <DotsActionMenu
                    smallDots
                    size={12}
                    position="bottom-end"
                    ariaLabel="Comment actions"
                    actions={{
                      edit: {
                        icon: (
                          <SVG
                            src="/assets/pencil.svg"
                            aria-hidden="true"
                          />
                        ),
                        onClick: handleEdit,
                        label: "Edit",
                      },
                      delete: {
                        icon: (
                          <SVG
                            src="/assets/delete.svg"
                            aria-hidden="true"
                          />
                        ),
                        destructive: true,
                        onClick: handleDelete,
                        label: "Delete",
                      },
                    }}
                  />
                )}
              </>
            )}
          </HoverActions>
        )}
      </CardHeaderRow>

      {/* Text content or edit mode */}
      {isEditing ? (
        <div style={{ alignSelf: "stretch" }}>
          <EditTextarea
            data-testid="user-comment-edit-textarea"
            value={editContent}
            autoFocus
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setEditContent(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <EditActions>
            <EditActionButton
              role="button"
              tabIndex={0}
              data-testid="user-comment-cancel-btn"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                handleCancel();
              }}
            >
              Cancel
            </EditActionButton>
            <EditActionButton
              role="button"
              tabIndex={0}
              data-testid="user-comment-save-btn"
              $primary
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                handleSave();
              }}
            >
              Save
            </EditActionButton>
          </EditActions>
        </div>
      ) : (
        hasContent && (
          <>
            <CardTextContent
              data-testid="user-comment-text"
              $truncate={!isExpanded}
            >
              <CommentCardMarkdown source={comment.content} />
            </CardTextContent>
            {isEdited && <EditedLabel data-testid="user-comment-edited-label">Edited</EditedLabel>}
          </>
        )
      )}

      {/* Replies rendered via the shared Reply component */}
      {isExpanded && visibleReplies.length > 0 && (
        <CommentReplies data-testid="user-comment-replies">
          {visibleReplies.map((reply: any) => (
            <Reply
              key={reply.id}
              reply={reply}
              setIsEditingReply={setIsEditingReply}
              isMobile={isMobile}
              commentLocation={commentLocation}
              forceOpen={isExpanded}
            />
          ))}
        </CommentReplies>
      )}

      {!isExpanded && visibleReplies.length > 0 && (
        <CollapsedReplyIndicator data-testid="user-comment-reply-indicator">
          {replyCountLabel}
        </CollapsedReplyIndicator>
      )}

      {/* ReplyBox — shown when expanded and not editing (matches Comment.js) */}
      {isExpanded && !showDeleteOnlyAction && (isMobile || (!isEditing && !isEditingReply)) && (
        <ReplyBox
          isMobile={isMobile}
          cancelComment={handleReplyCancel}
          submitComment={handleReplySubmit}
          newComment={newReply}
          setNewComment={setNewReply}
          newCommentInputBoxRef={newCommentInputBoxRef}
          placeholder={replyPlaceholder}
          buttonText="Reply"
          alwaysShowActionButtons={isMobile}
          isSubmitting={isSubmittingReply}
        />
      )}
      <DeleteCommentConfirmPortal
        open={showDeleteConfirm}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
      {isExpanded && canManage && showDeleteOnlyAction && !canUndo && (
        <div style={{ fontSize: 12, color: "var(--grey-2)", lineHeight: "16px", width: "100%" }}>We can't undo this comment because the document has changed.</div>
      )}
    </CardContainer>
  );
}

export default UserCommentCard;
